import {
  AdditionalityApproachType,
  AdditionalityApproachTypeToLabel,
  RegulatoryScheme,
  RegulatorySchemeToLabel,
} from '@safc/configuration';
import { FormAutocomplete } from 'components/ui/controlled-form/FormAutocomplete/index.js';
import { FormCountry } from 'components/ui/controlled-form/FormCountry/index.js';
import { FormDatePicker } from 'components/ui/controlled-form/FormDatePicker/index.js';
import { FormSelect } from 'components/ui/controlled-form/FormSelect/FormSelect.js';
import { FormInput } from 'components/ui/form/FormInput/index.js';
import dayjs from 'dayjs';
import type { Control } from 'react-hook-form';

import { FormHelp } from '../components/IssueForm.styled.js';
import { IssueFormRow } from '../components/IssueFormRow.js';
import { IssueFormYesNo } from '../components/IssueFormYesNo.js';

export const IssueFormBatch = ({
  control,
  shouldValidate,
  additionalityApproachType,
  regulatorySchemes,
}: { control: Control<any>; shouldValidate: boolean; additionalityApproachType: AdditionalityApproachType; regulatorySchemes: RegulatoryScheme[] | undefined }) => {
  return (
    <>
      <IssueFormRow control={control} field="batch.companyName">
        {props => <FormInput {...props} />}
      </IssueFormRow>
      <IssueFormRow control={control} field="batch.fuelProviderCompanyName">
        {props => <FormInput {...props} />}
      </IssueFormRow>
      <IssueFormRow control={control} field="batch.leaveDate">
        {props => (
          <FormDatePicker
            control={props.control}
            field={{ ...props.field, componentProps: { minDate: shouldValidate ? dayjs().subtract(91, 'day') : undefined } }}
            transform={{
              onChange: dayjs => dayjs.format('YYYY-MM-DD'),
              value: value => (value === undefined ? value : dayjs(value)),
            }}
          />
        )}
      </IssueFormRow>
      <IssueFormRow control={control} field="batch.batchNumber">
        {props => <FormInput {...props} />}
      </IssueFormRow>
      <IssueFormRow control={control} field="batch.productionYear">
        {props => (
          <FormDatePicker
            control={props.control}
            field={{ ...props.field, componentProps: { views: ['year'], disableFuture: shouldValidate ? true : false } }}
            transform={{
              onChange: dayjs => dayjs.year(),
              value: value => (value === undefined ? value : dayjs().set('year', value)),
            }}
          />
        )}
      </IssueFormRow>
      <IssueFormRow control={control} field="batch.upliftCountry">
        {props => (<FormCountry {...props} />)}
      </IssueFormRow>
      <IssueFormRow control={control} field="batch.upliftAirport">
        {props => <FormInput {...props} />}
      </IssueFormRow>
      <IssueFormRow control={control} field="batch.regulatorySchemesDeclaration">
        {props => (
          <FormAutocomplete
            control={props.control}
            field={{
              ...props.field,
              options: Object.values(RegulatoryScheme).map(scheme => ({
                value: scheme,
                label: RegulatorySchemeToLabel[scheme],
              })),
              componentProps: {
                multiple: true,
                freeSolo: true,
              },
            }}
          />
        )}
      </IssueFormRow>
      {(regulatorySchemes ?? []).includes(RegulatoryScheme.BritishColumbia) && (
        <FormHelp>
          To generate voluntary SAFc, in line with the SABA additionality approach,
          the regulated entity that reports the underlying SAF under
          {' '}
          <strong>British Columbia Low Carbon Fuel Standard</strong>
          {' '}
          must be able to demonstrate that the SAF component of the entity’s
          compliance obligation is met in the absence of the credits from voluntary SAF.
        </FormHelp>
      )}
      <IssueFormRow control={control} field="batch.additionalityApproach.type">
        {props => (
          <FormSelect
            control={props.control}
            field={{
              ...props.field,
              options: Object.values(AdditionalityApproachType).map(value => ({ value, label: AdditionalityApproachTypeToLabel[value] })),
            }}
          />
        )}
      </IssueFormRow>
      {additionalityApproachType === AdditionalityApproachType.NonSABA && (
        <>
          <IssueFormRow control={control} field="batch.additionalityApproach.approachName">
            {props => (<FormInput {...props} />)}
          </IssueFormRow>
          <IssueFormRow control={control} field="batch.additionalityApproach.allowScope3Claims">
            {props => (<IssueFormYesNo {...props} />)}
          </IssueFormRow>
        </>
      )}
    </>
  );
};
